<template>
  <BlokkliProvider
    v-if="hasNewNavigation"
    v-bind="blokkliProps"
    :edit-path
    class="page-menu sticky top-0 z-400 w-full bg-background"
    :class="{
      'h-[1000px]': isEditing,
    }"
  >
    <Transition :name="isMobile ? 'reveal-from-top' : ''">
      <div
        v-show="hasActiveMenu"
        class="page-menu-wrapper fixed inset-x-0 bottom-0 top-[var(--page-menu-bottom)] z-400 overflow-auto overscroll-none bg-background pb-[100px] tablet:relative tablet:inset-auto tablet:z-600 tablet:!block tablet:h-auto tablet:overflow-visible tablet:pb-0"
      >
        <div class="container">
          <div
            v-if="hasChannels || hasLanguages"
            class="page-menu-top flex items-center justify-between border-b border-b-border transition duration-500 tablet:opacity-100"
            :class="{
              'opacity-0': active !== 'menu',
            }"
          >
            <MenuChannels v-if="hasChannels" :channels :active-channel />
            <LanguageSwitcher v-if="hasLanguages" />
          </div>

          <MenuPopup
            v-if="searchEnabled"
            type="search"
            :visible="active === 'search'"
          >
            <MenuSearch :visible="active === 'search'" />
          </MenuPopup>
        </div>

        <MenuNavigation
          :visible="menuVisible"
          :quick-links="filterNullArray(quickLinks)"
          :teasers="filterNullArray(teasers)"
          :links
          @close="close"
        />
      </div>
    </Transition>

    <div
      ref="menuBottomEl"
      class="page-menu-bottom container relative z-500 flex items-center justify-between bg-background"
    >
      <Logo v-bind="logo" class="relative z-100" @click="close" />

      <div class="page-menu-buttons flex gap-1 tablet:gap-2">
        <MenuButton
          v-if="searchEnabled"
          id="search"
          v-model="active"
          icon="search-2"
          :title="$texts('search')"
        />
        <MenuButton
          id="menu"
          v-model="active"
          icon="menu"
          :title="$texts('menu')"
        />
      </div>
    </div>

    <Teleport to="body">
      <Transition name="fade-absolute">
        <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions -->
        <div
          v-if="!isEditing && (menuVisible || (hasActiveMenu && isMobile))"
          class="fixed left-0 top-0 z-300 h-screen w-screen bg-black/80 backdrop-blur-2xl"
          data-testid="page-menu-backdrop"
          @click="close"
        />
      </Transition>
    </Teleport>
  </BlokkliProvider>

  <StorageMenuOldNavigation v-else :links />
</template>

<script setup lang="ts">
import type {
  MenuLinkTreeFirstFragment,
  StorageMenuFragment,
} from '#graphql-operations'
import MenuButton from './Button/index.vue'
import MenuSearch from './Search/index.vue'
import MenuPopup from './Popup/index.vue'
import MenuNavigation from './Navigation/index.vue'
import MenuChannels from './Channels/index.vue'
import { DomainId } from '#graphql-operations'
import { filterNullArray } from '~/helpers'

const props = defineProps<StorageMenuFragment & { isEditing?: boolean }>()

const menuBottomEl = ref<HTMLElement>()
provide('menuBottomEl', menuBottomEl)

const {
  availableChannels: channels,
  activeChannel,
  isChannelLandingPage,
  availableLanguages,
  activeDomain,
} = useSiteContext()
const { $texts } = useEasyTexts()
const { logo } = useLogo()
const { isMobile } = useViewport()
const searchEnabled = useFeatureFlag('global_search', true)

// The currently active menu type.
const active = ref<'search' | 'menu' | ''>('')

const hasChannels = computed(() => channels.value.length > 1)
const hasLanguages = availableLanguages.length > 1
const menuVisible = computed(() => active.value === 'menu' || props.isEditing)
const hasActiveMenu = computed(() => !!active.value)
const links = computed<MenuLinkTreeFirstFragment[]>(
  () => props.menu?.links || [],
)

function close() {
  active.value = ''
}

onRouteChange((newRoute) => {
  // Don't close the menu when the user clicked on the link to switch the channel.
  if (isChannelLandingPage(newRoute.path)) {
    return
  }

  close()
})

// The path where blökkli editing happens.
// While the menu is visible on every page, editing is only possible
// on the /storage/:id route.
const editPath = computed(() => props.url?.path)

const hasNewNavigation = computed<boolean>(() => {
  return activeDomain === DomainId.zuerich_com
})

const htmlClasses = computed(() => {
  const classes = []
  if (hasChannels.value || (hasLanguages && hasNewNavigation.value)) {
    classes.push('has-top-menu')
  }
  if (active.value === 'menu') {
    classes.push('has-menu-open')
  }
  if (active.value === 'search') {
    classes.push('has-search-open')
  }
  if (hasNewNavigation.value) {
    classes.push('has-new-navigation')
  } else {
    classes.push('has-old-navigation')
  }

  return classes.join(' ')
})

useHead({
  htmlAttrs: {
    class: htmlClasses,
  },
})
</script>

<style lang="postcss">
html {
  --page-menu-top: 0px;
  --page-menu-bottom: 70px;
}

html.has-top-menu {
  --page-menu-top: 60px;
}
html.has-search-open {
  scroll-padding-top: 0;
}

.page-menu-bottom {
  height: var(--page-menu-bottom);

  /* The border below the header. This has to be a pseudo element due to z-index. */
  &:before {
    content: '';
    @apply pointer-events-none absolute bottom-0 left-0;
    @apply block h-[1px] w-full border-t border-t-border;
  }
}

.page-menu-buttons {
  height: var(--page-menu-bottom);
}

.page-menu-top {
  height: var(--page-menu-top);
}

.page-menu .logo-theme-bottom {
  @apply !text-scheme-invert;
}
</style>
